"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type MouseEvent,
  type ReactNode,
} from "react";
import { BrokenInAppBrowser, cannotEscape } from "./detect";
import { isBlockedDestination } from "./escapeAttempts";
import { runInAppEscape } from "./runEscape";

// Null means "this browser follows store links normally", so a tree with no
// provider behaves as it did before this workaround existed.
const InAppEscapeContext = createContext<BrokenInAppBrowser | null>(null);

export function InAppEscapeProvider({
  browser,
  children,
}: {
  browser: BrokenInAppBrowser | null;
  children: ReactNode;
}) {
  return (
    <InAppEscapeContext.Provider value={browser}>
      {children}
    </InAppEscapeContext.Provider>
  );
}

type StartEscapeOptions = {
  /**
   * Set when calling straight out of a tap handler. Awaiting anything before
   * the call spends the user activation and iOS then drops the navigation.
   */
  fromTap?: boolean;
  /** Called when the page is still here and should stop waiting. */
  onSettled?: () => void;
  /** Falls back to the click the reporter published for this page. */
  clickId?: string | null;
};

export type InAppEscape = {
  browser: BrokenInAppBrowser;
  /**
   * False when nothing was started, which is the common case even here: most
   * links point at a web page, and those load fine. The caller then does what
   * it does everywhere else.
   */
  start: (destination: string, options?: StartEscapeOptions) => boolean;
};

/**
 * Null unless the visitor is in a browser that refuses store URLs. Callers
 * branch on that once and are otherwise unchanged:
 *
 *   const inAppEscape = useInAppEscape();
 *   if (inAppEscape?.start(url, { fromTap: true })) return;
 *   window.location.replace(url);
 *
 * The disposer lives here so that no caller holds refs for a vendor bug.
 */
export function useInAppEscape(): InAppEscape | null {
  const browser = useContext(InAppEscapeContext);
  const disposeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      disposeRef.current?.();
      disposeRef.current = null;
    };
  }, []);

  return useMemo<InAppEscape | null>(() => {
    if (!browser) return null;

    return {
      browser,
      start: (destination, { fromTap = false, onSettled, clickId } = {}) => {
        if (!isBlockedDestination(destination)) return false;

        // Nothing to fire where every way out is refused; those visitors get
        // useMustOpenInBrowser instead.
        if (cannotEscape(browser)) return false;

        // A second tap restarts the chain rather than racing two of them.
        disposeRef.current?.();
        disposeRef.current = runInAppEscape({
          browser,
          destination,
          hasGesture: fromTap,
          onExhausted: onSettled,
          onReturned: onSettled,
          clickId,
        });
        return true;
      },
    };
  }, [browser]);
}

/**
 * True where no escape can work, so the only thing left is to tell the visitor
 * to leave the webview themselves. False in the webviews an escape works in.
 *
 * Gated on the destination like everything else here: a link these webviews
 * open perfectly well has nothing to warn about.
 */
export function useMustOpenInBrowser(
  destination: string | null | undefined
): boolean {
  const browser = useContext(InAppEscapeContext);

  if (browser === null || !destination) return false;
  return cannotEscape(browser) && isBlockedDestination(destination);
}

/**
 * onClick for an anchor pointing at a store, whose href these webviews refuse.
 *
 * Undefined everywhere else, so the anchor stays an ordinary link — including
 * with JS off, since the href itself is never replaced.
 */
export function useStoreLinkEscape(
  destination: string | null | undefined
): ((event: MouseEvent<HTMLAnchorElement>) => void) | undefined {
  const inAppEscape = useInAppEscape();

  return useMemo(() => {
    if (!inAppEscape || !destination) return undefined;
    // No handler at all for an href the webview opens normally.
    if (!isBlockedDestination(destination)) return undefined;

    return (event: MouseEvent<HTMLAnchorElement>) => {
      // preventDefault only once the escape has taken over, or a declined tap
      // would leave a dead button.
      if (!inAppEscape.start(destination, { fromTap: true })) return;
      event.preventDefault();
    };
  }, [inAppEscape, destination]);
}
